/**
 * @file Reads a `receivableAging` answer into the Receivable aging chart's
 * model: every one of `ANALYTICS_AGING_BUCKETS` in rank order, a bucket the
 * answer omits at zero invoices, each with an exact bar percent of the largest
 * bucket, and the count of outstanding invoices with no due date. Consumers:
 * `AnalyticsReceivableAgingChart` and `analyticsInvoiceTotals`. The no-due-date
 * count is the `invoices` of the row whose `bucket` is null, since the query
 * keeps only outstanding invoices. The amount follows the granted level
 * through `analyticsInvoiceAmount`; while it is refused, bars measure invoice
 * counts. No rows give no buckets, and a malformed row fails the whole model.
 */
import type { Snapshot } from 'valtio';

import { ANALYTICS_AGING_BUCKETS, analyticsDecimalFraction } from '#types';
import type { AnalyticsAgingBucket, NamedQueryCell } from '#types';

import { analyticsAgingBucketCell } from './analyticsAgingBucketCell';
import { analyticsBarMeasure } from './analyticsBarMeasure';
import { analyticsBarPercents } from './analyticsBarPercents';
import { analyticsInvoiceAmount } from './analyticsInvoiceAmount';
import { analyticsMalformedAnswer } from './analyticsMalformedAnswer';
import { analyticsRefusedColumn } from './analyticsRefusedColumn';
import { analyticsUnansweredFigure } from './analyticsUnansweredFigure';
import type {
  AnalyticsAgingBucketRow,
  AnalyticsInvoiceAmount,
  AnalyticsReceivableAging,
} from './analyticsInvoicingTab.types';
import type { AnalyticsAnswer } from './index.types';

type AgingRow = {
  bucket: AnalyticsAgingBucket | null;
  invoices: number;
  value: string | null;
};

const agingRow = (
  row: Snapshot<Record<string, NamedQueryCell>>,
  amount: AnalyticsInvoiceAmount,
): AgingRow | undefined => {
  const { invoices } = row;
  const value = row[amount.outstanding];
  const bucket = analyticsAgingBucketCell(row.bucket);
  const valid =
    bucket !== undefined &&
    typeof invoices === 'number' &&
    Number.isInteger(invoices) &&
    (value === null ||
      (typeof value === 'string' &&
        analyticsDecimalFraction(value) !== undefined));
  return valid ? { bucket, invoices, value } : undefined;
};

export const analyticsReceivableAging = (
  answer: Snapshot<AnalyticsAnswer> | undefined,
): AnalyticsReceivableAging => {
  if (answer?.status !== 'answered') return analyticsUnansweredFigure(answer);
  const { result } = answer;
  const amount = analyticsInvoiceAmount(result.level);
  const refusedValue = analyticsRefusedColumn(result, amount.outstanding);
  const rows = result.rows.map((row) => agingRow(row, amount));
  const parsed = rows.filter((row): row is AgingRow => row !== undefined);
  if (parsed.length !== rows.length)
    return analyticsMalformedAnswer('receivableAging');
  if (parsed.length === 0)
    return {
      status: 'answered',
      buckets: [],
      noDueDate: 0,
      amount,
      refusedValue,
    };
  const noDueDate = parsed.find((row) => row.bucket === null)?.invoices ?? 0;
  const buckets = ANALYTICS_AGING_BUCKETS.map(
    (bucket): Omit<AnalyticsAgingBucketRow, 'barPercent'> => {
      const row = parsed.find((r) => r.bucket === bucket);
      return row === undefined
        ? { bucket, invoices: 0, value: refusedValue === null ? '0' : null }
        : { bucket, invoices: row.invoices, value: row.value };
    },
  );
  const measures = buckets.map(({ invoices, value }) =>
    analyticsBarMeasure(value, invoices, refusedValue),
  );
  const barPercents = analyticsBarPercents(measures);
  return {
    status: 'answered',
    buckets: buckets.map((bucket, index) => ({
      ...bucket,
      barPercent: barPercents[index],
    })),
    noDueDate,
    amount,
    refusedValue,
  };
};
